import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CLIENTS_USERS_CREATE_URL, API_CLIENTS_USERS_EDIT_URL } from '../../constants/apiUrls';
import { CLIENTS_USERS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import FormEndOptions from '../forms/FormEndOptions';
import Switch from '../forms/Switch';
import TextField from '../forms/TextField';

interface ClientsUsersFormInputs {
  id?: number;
  clientId?: number;
  name?: string;
  email?: string;
  title?: string;
  phone?: string;
  address?: string;
  hasDashboardAccess?: boolean;
}

export const defaultValues: ClientsUsersFormInputs = {
  id: undefined,
  clientId: undefined,
  name: '',
  email: '',
  title: '',
  phone: '',
  address: '',
  hasDashboardAccess: false,
};

const normalizePayload = (clientId, form) => {
  const returnValue = {
    ...snakeCaseKeys({
      ...form,
      clientId,
    }),
    timeStamp: new Date().getTime(),
  };

  return returnValue;
};

const normalizeForm = (data) => {
  const returnValue = {
    ...camelCaseKeys(data),
  };

  return returnValue;
};

interface UsersFormProps {
  id?: number;
  clientId?: number;
}

function ClientsUsersForm(props: UsersFormProps) {
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();

  const { id: userId, clientId } = props;

  const isEdit = Boolean(userId) && Boolean(clientId);

  const form = useForm<ClientsUsersFormInputs>({
    defaultValues: {
      ...defaultValues,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;
  const navigate = useNavigate();

  useConfirmNavChange(() => isDirty);

  const onSubmit = async (fields) => {
    const payload = normalizePayload(clientId, fields);
    let response;
    if (isEdit) {
      response = await putRequestJSON(
        apiHost,
        generatePath(API_CLIENTS_USERS_EDIT_URL, {
          id: userId.toString(),
          '*': `?${queryString.stringify({
            client_id: clientId.toString(),
          })}`,
        }),
        payload
      );
    } else {
      response = await postRequestJSON(
        apiHost,
        generatePath(API_CLIENTS_USERS_CREATE_URL, { id: clientId.toString() }),
        payload
      );
    }

    // The server can return `error` or `errors` or `exception`
    let errors = get(response, 'errors');
    if (!errors) {
      errors = get(response, 'error');
    }
    if (!errors) {
      errors = get(response, 'exception');
    }

    if (errors) {
      let messages;
      // `errors` can be an array of strings, or an array.
      if (Array.isArray(errors)) {
        messages = errors;
      } else {
        messages = [errors];
      }
      showSnackBar(messages.join(' '), 'error', { event: 'user_save_error' });
      return;
    }

    const newUser = get(response, 'model');
    showSnackBar('Successfully saved', 'success', {
      event: isEdit ? 'user_updated' : 'user_created',
      user_id: isEdit ? userId : newUser?.id,
      client_id: clientId,
    });
    reset(normalizeForm(response?.model));

    // If we are creating a user, then go to the edit user URL on success.
    if (!isEdit && newUser.id) {
      setTimeout(() => {
        navigate(
          generatePath(CLIENTS_USERS_EDIT_URL, {
            id: clientId.toString(),
            userId: newUser.id.toString(),
          })
        );
      }, 1000);
    }
  };

  useEffect(() => {
    if (isEdit) {
      getRequestJSON(
        apiHost,
        generatePath(API_CLIENTS_USERS_EDIT_URL, {
          id: userId.toString(),
          '*': `?${queryString.stringify({
            client_id: clientId.toString(),
          })}`,
        })
      ).then((response) => {
        if (response.error) {
          showSnackBar(response.error, 'error', {
            event: 'user_load_error',
            user_id: userId,
            client_id: clientId,
          })
          return;
        }

        if (response.id) {
          const formValues = normalizeForm(response);
          reset(formValues);
        }
      });
    }
  }, [apiHost, isEdit, clientId, reset, userId, showSnackBar]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <Stack>
        <Box p={2}>
          <Box mt={2} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
            <Stack direction="row" alignItems="center">
              <Typography variant="h5">
                {isEdit ? 'Edit' : 'Create'} User {isEdit && `ID ${userId}`}
              </Typography>
            </Stack>
          </Box>
          <Box>
            <Box p={2} sx={{ background: 'white', borderRadius: '5px' }}>
              <Grid container>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label="Name"
                    rules={{
                      required: 'Please enter your name.',
                    }}
                    disabled
                  />
                </Grid>
              </Grid>
              <Grid container mt={2}>
                <Grid item xs={6}>
                  <TextField
                    name="email"
                    label="Email"
                    sx={{ width: '100%' }}
                    rules={{
                      required: 'Please enter your email.',
                    }}
                    disabled
                  />
                </Grid>
              </Grid>
              <Grid container mt={2}>
                <Grid item xs={6}>
                  <TextField name="title" label="Title" sx={{ width: '100%' }} />
                </Grid>
              </Grid>
              <Grid container mt={2}>
                <Grid item xs={6}>
                  <TextField name="phone" label="Phone" sx={{ width: '100%' }} />
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={10}>
                    <TextField name="address" label="Address" sx={{ width: '100%' }} />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={4}>
                    <Switch name="hasDashboardAccess" label="Dashboard Access" />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
        <FormEndOptions position="fixed">
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(-1)
              }
            >
              Back
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
              Save User
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default ClientsUsersForm;
